import { createContext, useContext } from 'react';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import type { MeResponse, OrgAdminOrganizationRef } from '@dermalink/shared';
import { getConfig } from './config.ts';

export type AuthUser = Awaited<ReturnType<typeof getCurrentUser>>;

export const AuthContext       = createContext<AuthUser | null>(null);
export const UserNameContext   = createContext<string>('');
export const IsDoctorContext   = createContext<boolean>(false);
export const IsOperatorContext = createContext<boolean>(false);
export const OrgAdminOfContext = createContext<OrgAdminOrganizationRef[]>([]);

export const useAuthUser  = () => useContext(AuthContext);
export const useUserName  = () => useContext(UserNameContext);
export const useIsDoctor  = () => useContext(IsDoctorContext);
export const useIsOperator = () => useContext(IsOperatorContext);
/** Practices the current user administers (empty when not an organisation administrator). */
export const useOrgAdminOf = () => useContext(OrgAdminOfContext);

/**
 * Returns the current Amplify ID token string, or an empty string if no
 * session exists. The API middleware expects an ID token (not an access token)
 * so that the email claim is available as a pre-verified JWT claim.
 */
export async function getAuthToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

/**
 * Builds a display string from profile first/last name fields.
 * Returns null when both are empty so callers can fall back to Cognito claims.
 */
export function displayNameFromProfileFields(
  firstName?: string | null,
  lastName?: string | null,
): string | null {
  const s = `${firstName ?? ''} ${lastName ?? ''}`.trim();
  return s || null;
}

/**
 * Resolves the display name from the current Amplify ID token claims.
 * Falls back through: full name → given + family name → email → empty string.
 */
export async function resolveDisplayName(): Promise<string> {
  try {
    const session = await fetchAuthSession();
    const payload = session.tokens?.idToken?.payload;
    if (!payload) return '';
    const name       = payload['name'] as string | undefined;
    const givenName  = payload['given_name'] as string | undefined;
    const familyName = payload['family_name'] as string | undefined;
    const email      = payload['email'] as string | undefined;
    return (
      name ||
      [givenName, familyName].filter(Boolean).join(' ') ||
      email ||
      ''
    );
  } catch {
    return '';
  }
}

/**
 * Display name for shell/header when opening routes without `requireProfile`
 * (e.g. /profile): prefers GET /profile names, else Cognito token claims.
 */
export async function resolveDisplayNamePreferApiProfile(apiBaseUrl: string): Promise<string> {
  const token = await getAuthToken();
  if (!token) return resolveDisplayName();
  try {
    const res = await fetch(`${apiBaseUrl}/profile`, {
      headers: { Authorization: `Bearer ${token}` },
      cache: 'no-store',
    });
    if (res.ok) {
      const p = (await res.json()) as { first_name?: string | null; last_name?: string | null };
      const fromApi = displayNameFromProfileFields(p.first_name, p.last_name);
      if (fromApi) return fromApi;
    }
  } catch {
    /* fall through */
  }
  return resolveDisplayName();
}

/**
 * Checks whether the current Cognito session belongs to the 'operators' group.
 * Reads the ID token claims directly — no API round-trip required.
 */
export async function resolveIsOperator(): Promise<boolean> {
  try {
    const session = await fetchAuthSession();
    const groups  = session.tokens?.idToken?.payload?.['cognito:groups'];
    return Array.isArray(groups) && groups.includes('operators');
  } catch {
    return false;
  }
}

/**
 * Determines the roles of the currently authenticated user by querying the API.
 *
 * Always fetches fresh — intentionally not cached — so that role changes on
 * the server (e.g. a user being added to or removed from doctor_profiles or
 * an organisation-admin flag) are reflected immediately on the next navigation or app load.
 */
export async function resolveUserRoles(): Promise<{ role: 'doctor' | 'patient'; orgAdminOf: OrgAdminOrganizationRef[] }> {
  try {
    const token = await getAuthToken();
    if (!token) return { role: 'patient', orgAdminOf: [] };
    const res = await fetch(`${getConfig().api_url}/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return { role: 'patient', orgAdminOf: [] };
    const data = await res.json() as MeResponse;
    return { role: data.isDoctor ? 'doctor' : 'patient', orgAdminOf: data.orgAdminOf };
  } catch {
    return { role: 'patient', orgAdminOf: [] };
  }
}

/** Returns 'doctor' if the user has a doctor profile, 'patient' otherwise. */
export async function resolveUserRole(): Promise<'doctor' | 'patient'> {
  return (await resolveUserRoles()).role;
}
